import { EventEmitter, Injectable, Injector } from '@angular/core';
import { ShepherdService } from 'angular-shepherd';
import { getWelcomeTourSteps } from './welcome-tour-steps';
import { getHomeTourSteps } from './home-tour-steps';
import { getHomeTourMobileSteps } from './home-tour-mobile-steps';
import { getSearchTourSteps } from './search-tour-steps';
import { getSearchTourMobileSteps } from './search-tour-mobile-steps';
import { getNotificationsTourSteps } from './notifications-tour-steps';
import { getNotificationsTourMobileSteps } from './notifications-tour-mobile-steps';
import { getFavoritesTourSteps } from './favorites-tour-steps';
import { getFavoritesTourMobileSteps } from './favorites-tour-mobile-steps';
import { HttpClient } from '@angular/common/http';
import { OperationResult } from '../model/operation-result.model';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { UserTour } from '../model/tours.model';
import { ConfigAssetLoaderService } from '../config/config-asset-loader.service';
import { InterceptorSkipHeader } from '../interceptor/interceptor.constants';
import { getMyFeedTourSteps } from './myFeed-tour-steps';
import { getMyFeedTourMobileSteps } from './myFeed-tour-mobile-steps';

@Injectable({
  providedIn: 'root',
})
export class TourService {
  private readonly DELIVERY_TOURS_API_BASE_URL;

  public welcomeTourCompleted = new EventEmitter<string>();

  constructor(
    private shepherdService: ShepherdService,
    private injector: Injector,
    private httpClient: HttpClient,
    private configService: ConfigAssetLoaderService
  ) {
    const deliveryServiceBaseUrl = this.configService.getConfig().deliveryServiceBaseUrl;
    this.DELIVERY_TOURS_API_BASE_URL = `${deliveryServiceBaseUrl}tours`;
  }

  getUserTours(): Observable<UserTour[]> {
    return this.httpClient.get<UserTour[]>(`${this.DELIVERY_TOURS_API_BASE_URL}/user`, InterceptorSkipHeader);
  }

  markTourAsViewed(id: string): Observable<any> {
    return this.httpClient.post(`${this.DELIVERY_TOURS_API_BASE_URL}/${id}/view`, InterceptorSkipHeader);
  }

  startWelcomeTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const steps = getWelcomeTourSteps(this.injector);
    this.shepherdService.addSteps(steps);
    const tourObject = this.shepherdService.tourObject;
    if (tourObject) {
      tourObject.on('complete', () => {
        console.log('Tour complete event triggered'); // Log pour vérifier
        this.welcomeTourCompleted.emit('welcome-tour');
      });
    }
    this.shepherdService.start();
  }

  startHomeTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#HomeStep1', () => {
      this.shepherdService.addSteps(getHomeTourSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startHomeMobileTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#HomeMobileStep1', () => {
      this.shepherdService.addSteps(getHomeTourMobileSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startMyFeedTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#MyFeedStep1', () => {
      this.shepherdService.addSteps(getMyFeedTourSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startMyFeedMobileTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#HomeMobileStep1', () => {
      this.shepherdService.addSteps(getMyFeedTourMobileSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startSearchTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#HomeStep3', () => {
      this.shepherdService.addSteps(getSearchTourSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startSearchMobileTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#HomeMobileStep3', () => {
      this.shepherdService.addSteps(getSearchTourMobileSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startNotificationsTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#NotificationsStep1', () => {
      this.shepherdService.addSteps(getNotificationsTourSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startNotificationsMobileTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#NotificationsMobileStep1', () => {
      this.shepherdService.addSteps(getNotificationsTourMobileSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startFavoritesTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#FavoritessStep1', () => {
      this.shepherdService.addSteps(getFavoritesTourSteps(this.injector));
      this.shepherdService.start();
    });
  }

  startFavoritesMobileTour() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      modalOverlayOpeningPadding: 5,
      modalOverlayOpeningRadius: 0,
      arrow: true,
    };
    const waitForElement = (selector: string, callback: () => void) => {
      const el = document.querySelector(selector);
      if (el) {
        callback();
      } else {
        setTimeout(() => waitForElement(selector, callback), 100);
      }
    };
    waitForElement('#NotificationsMobileStep1', () => {
      this.shepherdService.addSteps(getFavoritesTourMobileSteps(this.injector));
      this.shepherdService.start();
    });
  }
}
